import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate, Link } from 'react-router-dom';

import { styled } from '@mui/material/styles';
import { SPACING } from '../../styles/theme';

import { CvHeader } from '../CvHeader';
import {
  useUser,
  useNotification,
  useCase,
  useLocalStorage,
  useAgentExtension,
} from '../../hooks';
import { ExtensionDialog } from './ExtensionDialog';
import { STORAGE_CURRENT_CASE_ID } from '../../constants';
import { SnackBarNotificationOptions } from '../../types/SnackBarNotificationOptions';
import moment from 'moment';

export interface LayoutProps {
  headerTitle: string;
  children: React.ReactNode;
  viewActiveCase: string;
}

const StyledRoot = styled('div')(() => ({
  display: 'flex',
}));

const StyledContent = styled('main')(({ theme }) => ({
  flexGrow: 1,
  backgroundColor: theme.palette.background.default,
  padding: SPACING.CONTENT_PADDING,
}));

export const CvLayout = ({
  headerTitle,
  children,
  viewActiveCase,
}: LayoutProps) => {
  const navigate = useNavigate();
  const { withLocalStorage } = useLocalStorage();
  const { showNotification, closeSnackbar } = useNotification();
  const { userExtension, userProfile } = useUser();
  const {
    isOpenExtensionDialog,
    setIsOpenExtensionDialog,
    caseData,
    caseTerminated,
  } = useCase();
  const { agentExtensionLogsData } = useAgentExtension();

  const location = useLocation();
  const [caseId, setCaseId] = useState<any>();
  const [phoneNumber, setPhoneNumber] = useState<any>();
  const isOpen = true;
  const currentURL = location.pathname;

  const [isAgentExtensionExpired, setIsAgentExtensionExpired] =
    useState<boolean>(false);

  useEffect(() => {
    let extensionTimeout: any;

    if (
      agentExtensionLogsData &&
      agentExtensionLogsData.check_out_time &&
      agentExtensionLogsData.release_time
    ) {
      const checkOutDateTime = moment(
        new Date(agentExtensionLogsData.check_out_time),
      ).utc();

      const releaseDateTime = moment(
        new Date(agentExtensionLogsData.release_time),
      ).utc();

      if (!caseId && userExtension === agentExtensionLogsData.extension) {
        extensionTimeout = setInterval(() => {
          const currentDateTimeCase = moment(new Date()).utc();

          if (
            (currentDateTimeCase > checkOutDateTime &&
              currentDateTimeCase < releaseDateTime) ||
            (currentDateTimeCase > checkOutDateTime &&
              currentDateTimeCase > releaseDateTime) ||
            (currentDateTimeCase < checkOutDateTime &&
              currentDateTimeCase < releaseDateTime)
          ) {
            setIsAgentExtensionExpired(true);
          }
        }, 60000);
      }
    }

    return () => {
      // clear timeout when the component unmounts
      clearInterval(extensionTimeout);
    };
  }, [agentExtensionLogsData, caseId, userExtension]);

  useEffect(() => {
    closeSnackbar();
  }, [closeSnackbar, location.key]);

  // used for CV Portal incoming call / data pop up
  useEffect(() => {
    let isMounted = true;
    if (isMounted) {
      const currentCaseId = withLocalStorage(STORAGE_CURRENT_CASE_ID);
      if (
        userExtension &&
        userProfile &&
        userProfile.isComplete &&
        !isAgentExtensionExpired &&
        caseData &&
        caseData.status !== 0 &&
        caseData.callDetails.caseId !== currentCaseId &&
        caseTerminated === false
      ) {
        if (currentURL === '/app/settings') {
          const snackBaroptions: SnackBarNotificationOptions = {
            type: 'info',
            title: 'You have an incoming Connected Vehicle call.',
            showClose: false,
            preventDuplicate: false,
            additionalInfo: (
              <>
                Please click <Link to="/app/activecase">View Active Case</Link>
                &nbsp; to view case details.
              </>
            ),
          };
          showNotification(snackBaroptions);
        } else if (currentURL === '/app/landing') {
          navigate(`/app/activecase`);
        } else if (currentURL === '/app/activecase') {
          closeSnackbar();
          const snackBaroptions: SnackBarNotificationOptions = {
            type: 'info',
            title: `You have an incoming Connected Vehicle call. The screen is refreshed to reflect the associated information.`,
            showClose: true,
          };

          setTimeout(() => {
            showNotification(snackBaroptions);
          }, 2000);
        }
      }
    }
    return () => {
      isMounted = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [caseData, currentURL]);

  useEffect(() => {
    // Redirect to settings if profile is incomplete OR
    // all client platforms have empty userName and are marked not applicable
    const hasEmptyAndNotApplicableClientPlatforms =
      !!userProfile?.clientPlatform &&
      userProfile.clientPlatform.length > 0 &&
      userProfile.clientPlatform.every(
        cp =>
          (!cp.userName || cp.userName.trim() === '') &&
          cp.notApplicable === true,
      );

    if (
      userProfile &&
      (!userProfile.isComplete || hasEmptyAndNotApplicableClientPlatforms)
    ) {
      navigate(`/app/settings`);
      closeSnackbar();
      const snackBaroptions: SnackBarNotificationOptions = {
        type: 'info',
        title: `Please complete the Partner Information in Settings to proceed. Add a User Name or select Not Applicable. At least one Client Platform must have a valid User Name.`,
        showClose: true,
      };

      setTimeout(() => {
        showNotification(snackBaroptions);
      }, 1000);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userProfile]);

  useEffect(() => {
    const queryString = window.location.search;
    const urlParams = new URLSearchParams(queryString);
    setCaseId(urlParams.get('caseid'));
    setPhoneNumber(urlParams.get('phone_number'));
    if (caseId && caseId !== null) {
      navigate(`/app/activecase?caseid=${caseId}&phone_number=${phoneNumber}`);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [caseId, phoneNumber]);

  const closeExtensionDialog = () => {
    setIsOpenExtensionDialog(false);
  };

  return (
    <>
      {((!userExtension &&
        userProfile &&
        userProfile.isComplete &&
        currentURL !== '/app/settings') ||
        isOpenExtensionDialog) && (
        <ExtensionDialog
          isOpen={isOpen}
          agentExtension={isOpenExtensionDialog ? userExtension : ''}
          closeExtensionDialog={
            isOpenExtensionDialog ? closeExtensionDialog : undefined
          }
          data-testid="extensionDialog"
          requestFor={isOpenExtensionDialog ? 'E911CallNotCompleted' : ''}
        >
          {isOpenExtensionDialog && (
            <>
              <p style={{ marginBottom: '10px' }}>
                The 911 call could not be completed due to an incorrect
                extension. Please review your extension. Update the extension
                below if it is incorrect and try calling 911 again.
              </p>
              <p>
                If the extension you have provided at the login is accurate or
                you continue to receive this error, then notify the issue to the
                support team and use an alternate method to Call 911
              </p>
            </>
          )}
        </ExtensionDialog>
      )}
      {((userExtension && userProfile && userProfile.isComplete) ||
        currentURL === '/app/settings') && (
        <>
          <CvHeader
            title={headerTitle}
            viewActiveCase={viewActiveCase}
            navigateTo="/app/activecase"
          />
          <StyledRoot>
            <StyledContent>{children}</StyledContent>
          </StyledRoot>
        </>
      )}
    </>
  );
};
